import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
    Radio, Trophy, Play, Square, ExternalLink, Copy, Server, Smartphone,
    Tv, AlertCircle, CheckCircle2, RefreshCw, Eye, Video
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LiveStreamingTab = ({ token }) => {
    const [provider, setProvider] = useState(null);
    const [tournaments, setTournaments] = useState([]);
    const [selectedTournament, setSelectedTournament] = useState('');
    const [matches, setMatches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [cfDetails, setCfDetails] = useState({}); // { match_id: cloudflareResponse }

    const auth = { headers: { Authorization: `Bearer ${token}` } };

    const fetchProvider = useCallback(async () => {
        try {
            const r = await axios.get(`${API_URL}/api/streaming/provider`);
            setProvider(r.data);
        } catch (e) { /* ignore */ }
    }, []);

    const fetchTournaments = useCallback(async () => {
        try {
            const r = await axios.get(`${API_URL}/api/streaming/tournaments`, auth);
            setTournaments(r.data || []);
            if (!selectedTournament && r.data?.length) setSelectedTournament(r.data[0].id);
        } catch (e) {
            toast.error('Failed to load tournaments');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const fetchMatches = useCallback(async () => {
        if (!selectedTournament) return;
        setLoading(true);
        try {
            const r = await axios.get(
                `${API_URL}/api/streaming/tournaments/${selectedTournament}/matches`, auth
            );
            setMatches(r.data?.matches || []);
        } catch (e) {
            toast.error('Failed to load matches');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTournament, token]);

    useEffect(() => { fetchProvider(); fetchTournaments(); }, [fetchProvider, fetchTournaments]);
    useEffect(() => { fetchMatches(); }, [fetchMatches]);

    // Poll match streaming state every 8s while tab is open
    useEffect(() => {
        const id = setInterval(fetchMatches, 8000);
        return () => clearInterval(id);
    }, [fetchMatches]);

    const startCloudflare = async (matchId) => {
        try {
            const r = await axios.post(
                `${API_URL}/api/streaming/match/${matchId}/cloudflare/start`, {}, auth
            );
            setCfDetails(prev => ({ ...prev, [matchId]: r.data }));
            toast.success('Cloudflare live input ready');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Cloudflare start failed');
        }
    };

    const stopCloudflare = async (matchId) => {
        try {
            await axios.post(`${API_URL}/api/streaming/match/${matchId}/cloudflare/stop`, {}, auth);
            setCfDetails(prev => { const cp = { ...prev }; delete cp[matchId]; return cp; });
            toast.success('Cloudflare stream stopped');
            fetchMatches();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Cloudflare stop failed');
        }
    };

    const copyLink = async (text, label) => {
        try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
        catch { toast.error('Could not copy'); }
    };

    return (
        <div className="space-y-6" data-testid="admin-live-streaming-tab">
            {/* Provider banner */}
            <Card className="bg-[#0d2847] border-white/10 p-5">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${provider?.cloudflare_configured ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
                            {provider?.cloudflare_configured
                                ? <CheckCircle2 className="w-5 h-5 text-green-400" />
                                : <AlertCircle className="w-5 h-5 text-yellow-400" />}
                        </div>
                        <div>
                            <div className="font-semibold flex items-center gap-2">
                                Streaming Provider:{' '}
                                <span className={provider?.cloudflare_configured ? 'text-green-400' : 'text-yellow-400'}>
                                    {provider?.active_provider || '...'}
                                </span>
                            </div>
                            <div className="text-xs text-muted-foreground max-w-2xl">{provider?.advice}</div>
                        </div>
                    </div>
                    <div className="flex gap-2 text-xs">
                        <span className="flex items-center gap-1 px-2 py-1 bg-white/5 rounded">
                            <Smartphone className="w-3 h-3" /> Phone (browser): ✓
                        </span>
                        <span className={`flex items-center gap-1 px-2 py-1 rounded ${provider?.supports_obs_rtmp ? 'bg-green-500/10 text-green-400' : 'bg-white/5 text-muted-foreground'}`}>
                            <Server className="w-3 h-3" /> OBS / RTMP: {provider?.supports_obs_rtmp ? '✓' : '—'}
                        </span>
                        <span className={`flex items-center gap-1 px-2 py-1 rounded ${provider?.scales_to_millions ? 'bg-green-500/10 text-green-400' : 'bg-white/5 text-muted-foreground'}`}>
                            <Tv className="w-3 h-3" /> CDN scale: {provider?.scales_to_millions ? '✓' : 'limited'}
                        </span>
                    </div>
                </div>
            </Card>

            {/* Tournament selector */}
            <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[260px] max-w-md">
                    <Label className="text-xs text-muted-foreground">Tournament</Label>
                    <Select value={selectedTournament} onValueChange={setSelectedTournament}>
                        <SelectTrigger className="bg-[#0d2847] border-white/10" data-testid="streaming-tournament-select">
                            <SelectValue placeholder="Pick a tournament" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0d2847] border-white/10 max-h-80">
                            {tournaments.map(t => (
                                <SelectItem key={t.id} value={t.id}>
                                    {t.name} — {t.sport}
                                    {t.active_stream_count > 0 && (
                                        <span className="ml-2 text-red-400">● {t.active_stream_count} live</span>
                                    )}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <Button onClick={fetchMatches} variant="outline" className="border-white/10" data-testid="streaming-refresh-btn">
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
            </div>

            {/* Matches list */}
            {!selectedTournament ? (
                <Card className="bg-[#0d2847] border-white/10 p-8 text-center text-muted-foreground">
                    Pick a tournament to manage its live streams.
                </Card>
            ) : matches.length === 0 ? (
                <Card className="bg-[#0d2847] border-white/10 p-8 text-center text-muted-foreground">
                    No matches found for this tournament.
                </Card>
            ) : (
                <div className="space-y-3">
                    {matches.map(m => (
                        <MatchStreamRow
                            key={m.id}
                            match={m}
                            cfConfigured={!!provider?.cloudflare_configured}
                            cfData={cfDetails[m.id]}
                            onStartCloudflare={() => startCloudflare(m.id)}
                            onStopCloudflare={() => stopCloudflare(m.id)}
                            onCopy={copyLink}
                        />
                    ))}
                </div>
            )}

            {/* Help section */}
            <Card className="bg-cyan-500/5 border-cyan-500/30 p-5">
                <h4 className="font-semibold mb-3 flex items-center gap-2"><Video className="w-4 h-4 text-cyan-400" /> How streaming works</h4>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-1">
                        <div className="font-semibold flex items-center gap-2 text-cyan-400">
                            <Smartphone className="w-4 h-4" /> Minimum: a phone
                        </div>
                        <p className="text-muted-foreground text-xs">
                            Click <strong>Go Live (Browser)</strong> on any match. Allow camera + mic.
                            That's it — viewers hit the share link and watch instantly.
                        </p>
                    </div>
                    <div className="space-y-1">
                        <div className="font-semibold flex items-center gap-2 text-cyan-400">
                            <Server className="w-4 h-4" /> Maximum: pro broadcast
                        </div>
                        <p className="text-muted-foreground text-xs">
                            {provider?.cloudflare_configured ? (
                                <>Click <strong>Open RTMP Credentials</strong>, paste the URL + key into OBS / Blackmagic / vMix.
                                Recording is automatic; replays are ready ~60s after the stream ends.</>
                            ) : (
                                <>Set <code>CLOUDFLARE_ACCOUNT_ID</code> + <code>CLOUDFLARE_API_TOKEN</code> in <code>backend/.env</code> to enable
                                RTMP/SRT ingest, global CDN delivery and auto-recording.</>
                            )}
                        </p>
                    </div>
                </div>
            </Card>
        </div>
    );
};


const MatchStreamRow = ({ match, cfConfigured, cfData, onStartCloudflare, onStopCloudflare, onCopy }) => {
    const [showCf, setShowCf] = useState(false);
    const stream = match.stream || {};
    const isStreaming = stream.is_streaming;

    const consoleUrl = `/live-console/${match.id}`;
    const watchUrl = `${window.location.origin}/live-watch/${match.id}`;

    return (
        <Card className="bg-[#0d2847] border-white/10 p-4" data-testid={`stream-row-${match.id}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 mb-1">
                        {isStreaming && (
                            <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 rounded-full animate-pulse flex items-center gap-1">
                                <span className="w-1.5 h-1.5 bg-red-500 rounded-full" /> LIVE
                            </span>
                        )}
                        <span className="text-xs px-2 py-0.5 bg-white/10 rounded uppercase">{match.status}</span>
                        {match.round && <span className="text-xs text-muted-foreground">{match.round}</span>}
                    </div>
                    <div className="font-semibold">
                        {match.team1 || match.home_team} <span className="text-muted-foreground">vs</span> {match.team2 || match.away_team}
                    </div>
                    <div className="text-xs text-muted-foreground">
                        {match.venue || '—'}
                        {stream.viewer_count_webrtc > 0 && (
                            <span className="ml-3 inline-flex items-center gap-1 text-cyan-400">
                                <Eye className="w-3 h-3" /> {stream.viewer_count_webrtc} watching
                            </span>
                        )}
                        {stream.provider && <span className="ml-3 text-cyan-400">via {stream.provider}</span>}
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    <Link to={consoleUrl} target="_blank">
                        <Button size="sm" className="bg-cyan-500 hover:bg-cyan-600 text-black" data-testid={`go-live-${match.id}`}>
                            <Play className="w-3 h-3 mr-1" /> Go Live (Browser)
                        </Button>
                    </Link>
                    <Button size="sm" variant="outline" className="border-white/10" onClick={() => onCopy(watchUrl, 'Viewer link')} data-testid={`copy-link-${match.id}`}>
                        <Copy className="w-3 h-3 mr-1" /> Copy Viewer Link
                    </Button>
                    <Link to={`/live-watch/${match.id}`} target="_blank">
                        <Button size="sm" variant="outline" className="border-white/10">
                            <ExternalLink className="w-3 h-3 mr-1" /> Open Viewer
                        </Button>
                    </Link>
                    {cfConfigured && (
                        <>
                            {(cfData || stream.cloudflare_uid) ? (
                                <>
                                    <Button size="sm" variant="outline" className="border-white/10" onClick={() => setShowCf(!showCf)}>
                                        <Server className="w-3 h-3 mr-1" /> RTMP Credentials
                                    </Button>
                                    <Button size="sm" variant="outline" className="border-red-500/40 text-red-400" onClick={onStopCloudflare}>
                                        <Square className="w-3 h-3 mr-1" /> Stop CF
                                    </Button>
                                </>
                            ) : (
                                <Button size="sm" variant="outline" className="border-white/10" onClick={onStartCloudflare} data-testid={`cf-start-${match.id}`}>
                                    <Server className="w-3 h-3 mr-1" /> Open RTMP Credentials
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {showCf && cfData && (
                <div className="mt-4 p-4 bg-[#0a1628] border border-white/10 rounded space-y-3 text-xs" data-testid={`cf-creds-${match.id}`}>
                    <div className="font-semibold text-sm flex items-center gap-2 text-cyan-400">
                        <Server className="w-4 h-4" /> Cloudflare Stream — Pro Ingest
                    </div>
                    <CredRow label="RTMPS URL" value={cfData.ingest?.rtmps?.url} onCopy={onCopy} />
                    <CredRow label="RTMPS Stream Key" value={cfData.ingest?.rtmps?.stream_key} onCopy={onCopy} secret />
                    <CredRow label="SRT URL" value={cfData.ingest?.srt?.url} onCopy={onCopy} />
                    <CredRow label="SRT Stream ID" value={cfData.ingest?.srt?.stream_id} onCopy={onCopy} />
                    <CredRow label="HLS Playback URL" value={cfData.playback?.hls} onCopy={onCopy} />
                    <p className="text-muted-foreground pt-1">
                        OBS → Settings → Stream → Service: <strong>Custom</strong>. Paste the RTMPS URL into <strong>Server</strong> and the Stream Key into <strong>Stream Key</strong>. Hit Start Streaming.
                    </p>
                </div>
            )}
        </Card>
    );
};


const CredRow = ({ label, value, onCopy, secret }) => {
    const [reveal, setReveal] = useState(false);
    if (!value) return null;
    const display = secret && !reveal ? '••••••••••••••••' : value;
    return (
        <div className="flex items-center gap-2">
            <span className="text-muted-foreground w-32 flex-shrink-0">{label}:</span>
            <code className="flex-1 bg-white/5 px-2 py-1 rounded break-all">{display}</code>
            {secret && (
                <Button size="sm" variant="ghost" className="h-7" onClick={() => setReveal(!reveal)}>
                    {reveal ? 'Hide' : 'Show'}
                </Button>
            )}
            <Button size="sm" variant="ghost" className="h-7" onClick={() => onCopy(value, label)}>
                <Copy className="w-3 h-3" />
            </Button>
        </div>
    );
};

export default LiveStreamingTab;
